import React from "react";
import StepModel from "../../../model/configurator/StepModel";
import BaseStep from "../../shared/steps/BaseStep";
import formModelStore from "../../../store/FormModelStore";
import ExpandableContainer from "../../configuration/steps/ExpandableContainer";
import { Calculation, Selection } from "../../../../src/application/Contracts";
import WaterSystemTechnicalDataModel = Calculation.WaterSystemTechnicalDataModel;
import TankTechnicalData = Selection.TankTechnicalDataModel;
import { WaterSystemTechnicalData } from "./water-system/WaterSystemTechnicalData";
import { RenderStepInputReadonlyField } from "./custom-fields/Helper";
import { TankSelectionTechnicalData } from "./tank-selection/TankSelectionTechnicalData";
import { MatchingTapWaterSystemSelection } from "./summary/MatchingTapWaterSystemSelection";
import Field from "../../../model/configurator/Field";
import TwsInputMethodField from "./custom-fields/tws-input-method/TwsInputMethodField";
import TwsInstallationTypeField from "./custom-fields/tws-installation-type/TwsInstallationTypeField";
import TwsMatchingUnitField from "./custom-fields/tws-matching-units/TwsMatchingUnitField";
import TwsProductCatalogSelectProductField
    from "./custom-fields/tws-product-catalog-select-product/TwsProductCatalogSelectProductField";
import {
    TwsProductCatalogSelectProduct
} from "./custom-fields/tws-product-catalog-select-product/TwsProductCatalogSelectProduct";
import TWSCalculationApiService from "../../../api-services/TWSCalculationApiService";
import APIServicesRegistry from "../../../api-services/APIServicesRegistry";
import flattenConfigurationModel from "../../../flattenConfigurationModel";
import TWSSummaryImages = Calculation.TWSSummaryImages;
import WaterSystemOptionModel = Calculation.WaterSystemOptionModel;
import { GetOptionsWeight, GetTWSWeight, GetTankWeight, TankOnSite } from "@lib/components/utils/FormModelStoreTWSHelper";

interface SelfState {
    matchingWaterSystemTechnicalData: WaterSystemTechnicalDataModel | null;
    technicalData: TankTechnicalData | null;
    inputMethodResults: any;
    capacityCalculation: any;
    selectedTank: Selection.TankResultModel | null;
    selectedWaterSystem: any;
    summaryImages: TWSSummaryImages | null;
    selectedOptions: WaterSystemOptionModel[];
}

export class SummaryStep extends BaseStep {
    _tWSCalculationApiService: TWSCalculationApiService | null = null;
    state: SelfState = {
        matchingWaterSystemTechnicalData: null,
        technicalData: null,
        inputMethodResults: null,
        capacityCalculation: null,
        selectedTank: null,
        selectedWaterSystem: null,
        summaryImages: null,
        selectedOptions: []
    }

    constructor(props: any) {
        super(props);

        window.scheduleAtDocumentReady(() => {
            this._tWSCalculationApiService = APIServicesRegistry.getService(TWSCalculationApiService);
        })
    }

    static getStepModel(model: any) {
        return new StepModel('summary', model)
    }

    static getFields(step: StepModel) {

        const modifiedStep = { ...step, name: 'inputMethod' }
        const createField = (name: string, required: boolean = false) => new Field(name, modifiedStep, true, formModelStore, required)
        return {
            inputMethod: new TwsInputMethodField("inputMethod", modifiedStep, true, formModelStore),
            installationType: new TwsInstallationTypeField("installationType", modifiedStep, true, formModelStore),
            maxChargingTimeForAccumulation: createField("installationTypeCalculation.maxChargingTimeForAccumulation"),
            maxChargingTimeForAccumulationRestaurants: createField("installationTypeCalculation.maxChargingTimeForAccumulation"),
            maxChargingTimeForAccumulationSportCenters: createField("installationTypeCalculation.maxChargingTimeForAccumulation"),
            tDhw: createField("installationTypeCalculation.tDhw", true),
            park: createField("installationTypeCalculation.park"),
            standing: createField("installationTypeCalculation.standing"),
            situation: createField("installationTypeCalculation.situation"),
            apartmentsStudio: createField("installationTypeCalculation.apartmentsStudio"),
            apartmentsT1Room: createField("installationTypeCalculation.apartmentsT1Room"),
            apartmentsT2Room: createField("installationTypeCalculation.apartmentsT2Room"),
            apartmentsT3Room: createField("installationTypeCalculation.apartmentsT3Room"),
            apartmentsT4Room: createField("installationTypeCalculation.apartmentsT4Room"),
            apartmentsT5Room: createField("installationTypeCalculation.apartmentsT5Room"),
            apartmentsT6Room: createField("installationTypeCalculation.apartmentsT6Room"),
            primaryTankChargingPtDt: createField("installationTypeCalculation.primaryTankChargingPtDt"),
            primaryTankAquaEfficiency: createField("installationTypeCalculation.primaryTankAquaEfficiency"),
            primaryTankAvailableCapacity: createField("installationTypeCalculation.primaryTankAvailableCapacity"),
            madeOf: createField("installationTypeCalculation.madeOf"),
            primaryStorage: createField("installationTypeCalculation.primaryStorage"),
            unitCapacity: createField("installationTypeCalculation.unitCapacity"),
            matchingUnit: new TwsMatchingUnitField("installationTypeCalculation.selectedUnitModel", modifiedStep, true, formModelStore),
            numberOfBeds: createField("installationTypeCalculation.numberOfBeds", true),
            numberOfShowerRooms: createField("installationTypeCalculation.numberOfShowerRooms", true),
            numberOfShowersPerShowerRoom: createField("installationTypeCalculation.numberOfShowersPerShowerRoom", true),
            showerConsumption: createField("installationTypeCalculation.showerConsumption", true),
            showerMeanDuration: createField("installationTypeCalculation.showerMeanDuration", true),
            numberMealsBreakfast: createField("installationTypeCalculation.numberMealsBreakfast", true),
            numberMealsLunch: createField("installationTypeCalculation.numberMealsLunch", true),
            numberMealsDinner: createField("installationTypeCalculation.numberMealsDinner", true),
            restaurantCategory: createField("installationTypeCalculation.restaurantCategory"),
            numberOfRooms: createField("installationTypeCalculation.numberOfRooms", true),
            hotelStanding: createField("installationTypeCalculation.hotelStanding"),
            mountain: createField("installationTypeCalculation.mountain"),
            bedsPerRoom: createField("installationTypeCalculation.bedsPerRoom", true),
            campingClassification: createField("installationTypeCalculation.campingClassification"),
            campingSituation: createField("installationTypeCalculation.campingSituation"),
            sportCenterGymnasiumNumberOfSportHalls: createField("installationTypeCalculation.sportCenterGymnasiumNumberOfSportHalls", true),
            sportCenterStadiumNumberOfSportGrounds: createField("installationTypeCalculation.sportCenterStadiumNumberOfSportGrounds", true),
            capacityCapacity: createField("capacityCalculation.capacity", true),
            capacityVolume: createField("capacityCalculation.volume", true),
            capacityPrimaryTank: createField("capacityCalculation.primaryTank"),
            capacitySecondaryTank: createField("capacityCalculation.secondaryTank"),
            productCatalogProductFamilies: new TwsProductCatalogSelectProductField("productCatalogCalculation.availableProducts", modifiedStep, true, formModelStore),
            selectedProduct: new TwsProductCatalogSelectProductField("productCatalogCalculation.selectedProduct", modifiedStep, true, formModelStore),
            temperatureProgram: createField("productCatalogCalculation.temperatureProgram"),
            capacityInstantaneous: createField("capacityCalculation.instantaneous"),
            capacitySemiInstantaneous: createField("capacityCalculation.semiInstantaneous"),
            pushButtons: createField("installationTypeCalculation.pushButtons"),
        }
    }

    render() {
        return <>
            <div className={`generic-step-content`}>
                <div className="basic-step-area-container">
                    <div className={`card`}>
                        <div className={`card-body`}>
                            {this.renderArticleNumbersSum()}
                            {this.renderInputSummarySection()}
                            {!this.onlyTankSelectionAvailable() && this.renderTwsTechnicalData()}
                            {this.renderTankTechnicalData()}
                            {this.renderFlowchart()}
                            {this.renderGeneralTankDescription()}
                            {this.renderTwsGeneralDescription()}
                            {this.renderProductPictures()}
                            {this.renderTechnicalDrawings()}
                        </div>
                    </div>
                </div>
            </div>
        </>;
    }

    componentDidUpdate(prevProps: Readonly<any>, prevState: Readonly<{}>, snapshot?: any) {
        if (!prevProps.visible && this.props.visible) {
            this.props.setLastAvailableStep(this.props.stepSlug);
            this.setState({
                capacityCalculation: formModelStore.formModel.inputMethod?.capacityCalculation,
                inputMethodResults: formModelStore.formModel.inputMethod?.results,
                matchingWaterSystemTechnicalData: formModelStore.formModel.waterSystem?.results?.value?.matchingWaterSystemTechnicalDataModel,
                technicalData: formModelStore.formModel.tankSelection?.results?.value?.tankTechnicalData,
                selectedTank: formModelStore.formModel.tankSelection?.selectedTank?.value,
                selectedWaterSystem: formModelStore.formModel.waterSystem?.selectedTapWaterSystemModel?.value,
                selectedOptions: prevProps.model.waterSystem?.selectedTWSOptions?.value
            });
            this.props.stepChangeCallback(this.props.stepSlug, formModelStore.formModel.tankSelection.selectedTank?.value?.ref);
            this._tWSCalculationApiService?.getSummaryImages(flattenConfigurationModel(formModelStore).formModel)
                .then((response: any) => {
                    this.setState({ summaryImages: response.data })
                    console.warn(response.data)
                })
        }
    }

    private renderArticleNumbersSum() {
        return <>
            <ExpandableContainer arrowFirst={true}
                label={__('Article numbers sum-up')}
                initialOpen={true}>
                <div className="tws-installation-types">
                    {!this.onlyTankSelectionAvailable() && RenderStepInputReadonlyField("TWS", this.state.matchingWaterSystemTechnicalData?.productArticleNumber)}
                    {formModelStore.formModel.waterSystem?.selectedTWSOptions?.value?.length ?
                        RenderStepInputReadonlyField("Options", formModelStore.formModel.waterSystem?.selectedTWSOptions?.value?.map((option: any) => option.artNo).join(', ')) : ''}
                    {(this.isTankAvailable() || TankOnSite()) && this.renderTankArticleNumbers()}
                </div>
            </ExpandableContainer>
        </>
    }

    private onlyTankSelectionAvailable = (): boolean => {
        return formModelStore.formModel.inputMethod?.productCatalogCalculation?.selectedProduct?.value?.isTank;
    }

    private renderTankArticleNumbers() {
        if (this.isTankAvailable())
            return <>
                {RenderStepInputReadonlyField("Aquatank", this.state.technicalData?.articleName)}
                {RenderStepInputReadonlyField("Total weight",
                    `${GetTWSWeight() + GetTankWeight() + GetOptionsWeight(this.state.selectedOptions)}kg`)}
            </>

        if (TankOnSite())
            return <>
                {RenderStepInputReadonlyField("Aquatank", __('Tank on site'))}
                {RenderStepInputReadonlyField("Total weight",
                    `${GetTWSWeight() + GetOptionsWeight(this.state.selectedOptions)}kg`)}
            </>
    }

    private isTankAvailable(): boolean {
        return !!this.state.selectedTank;
    }

    private renderTwsTechnicalData() {
        return <ExpandableContainer arrowFirst={true}
            label={__('TWS technical data')}
            initialOpen={true}>
            {this.renderWaterSystemTechnicalData()}
        </ExpandableContainer>
    }

    private renderWaterSystemTechnicalData(): any {
        return <>
            {this.state.matchingWaterSystemTechnicalData?.productFamily &&
                <WaterSystemTechnicalData {...this.state.matchingWaterSystemTechnicalData} grid={4} optionsWeight={GetOptionsWeight(this.state.selectedOptions)} tankWeight={GetTankWeight()} />}
        </>
    }


    private renderInputSummarySection() {
        switch (formModelStore.formModel.inputMethod?.inputMethod?.value) {
            case Calculation.InputMethod.Capacity:
                return this.renderCapacityInputSelection();
            case Calculation.InputMethod.ProductCatalog:
                return this.renderProductSelection();
            case Calculation.InputMethod.InstallationType:
                return this.renderMatchingTapWaterSystemSelection();
            default:
                return <div>EMPTY</div>
        }
    }

    private renderMatchingTapWaterSystemSelection() {
        return <>
            {this.state.inputMethodResults &&
                <ExpandableContainer arrowFirst={true}
                    label={__('Input method selection')}
                    initialOpen={true}>
                    <MatchingTapWaterSystemSelection
                        equivalentInStandardApartments={formModelStore.formModel.inputMethod?.installationTypeCalculation?.equivalentInStandardApartments}
                        renderField={this.renderField}
                        readonly={true}
                        fields={this.fields}
                        selectedInstallationType={formModelStore.formModel.inputMethod?.installationType.value}
                    />
                </ExpandableContainer>
            }
        </>
    }

    private renderProductSelection() {
        return <ExpandableContainer arrowFirst={true}
            label={__('Input method selection')}
            initialOpen={true}>
            <div className="step-inputs-row" style={{ height: 200 }}>
                <div className={`tws-product-family`}>
                    <div className={`description`}>
                        <div className={`name`}>
                            {formModelStore.formModel.inputMethod?.productCatalogCalculation?.selectedProduct?.value?.displayName}
                            <div className={`title`}>
                                {formModelStore.formModel.inputMethod?.productCatalogCalculation?.selectedProduct?.value?.title}
                            </div>
                        </div>
                        {TwsProductCatalogSelectProduct.renderInformationSection(formModelStore.formModel.inputMethod?.productCatalogCalculation?.selectedProduct?.value)}
                    </div>
                    {formModelStore.formModel.inputMethod?.productCatalogCalculation?.selectedProduct?.value?.image && <div className={`image`} style={{ backgroundImage: `url(${formModelStore.formModel.inputMethod?.productCatalogCalculation?.selectedProduct?.value?.image})` }} />}
                </div>
            </div>
        </ExpandableContainer>
    }

    private renderCapacityInputSelection() {
        return <>
            {this.state.capacityCalculation &&
                <ExpandableContainer arrowFirst={true}
                    label={__('Input method selection')}
                    initialOpen={true}>
                    <div className="step-inputs-row grid-2">
                        {RenderStepInputReadonlyField("Capacity",
                            formModelStore.formModel.inputMethod?.capacityCalculation?.capacity?.value, "kW")}
                        {(formModelStore.formModel.inputMethod?.capacityCalculation?.primaryTank?.value ||
                            formModelStore.formModel.inputMethod?.capacityCalculation?.semiInstantaneous?.value) &&
                            RenderStepInputReadonlyField("Volume",
                                formModelStore.formModel.inputMethod?.capacityCalculation?.volume?.value, "L")}
                        {RenderStepInputReadonlyField("Instantaneous",
                            formModelStore.formModel.inputMethod?.capacityCalculation?.instantaneous?.value ? __("Yes") : __("No"))}
                        {RenderStepInputReadonlyField("Primary tank",
                            formModelStore.formModel.inputMethod?.capacityCalculation?.primaryTank?.value ? __("Yes") : __("No"))}
                        {RenderStepInputReadonlyField("Semi-instantaneous",
                            formModelStore.formModel.inputMethod?.capacityCalculation?.semiInstantaneous?.value ? __("Yes") : __("No"))}
                    </div>
                </ExpandableContainer>
            }
        </>
    }

    private renderTankTechnicalData(): any {
        if (!this.isTankAvailable()) return <></>
        return <>
            <ExpandableContainer arrowFirst={true}
                label={__('Tank technical data')}
                initialOpen={true}>
                {this.state.technicalData != null && <TankSelectionTechnicalData {...this.state.technicalData} />}
            </ExpandableContainer>
        </>
    }

    private renderFlowchart() {
        if (!this.state.summaryImages?.flowChart) return <></>;
        return <>{
            <ExpandableContainer arrowFirst={true}
                label={__('Flowchart')}
                initialOpen={true}>
                <div className={'summary-flowchart-container'}>
                    {<div className={'tws-summary-product-background-picture'} style={{ backgroundImage: `url(${this.state.summaryImages?.flowChart})` }} />}
                </div>
            </ExpandableContainer>}
        </>
    }

    private renderGeneralTankDescription() {
        if (!this.isTankAvailable()) return <></>
        const tankDescription = this.state.selectedTank?.description;
        return <>
            {tankDescription &&
                <ExpandableContainer arrowFirst={true}
                    label={__('General tank description')}
                    initialOpen={true}>
                    <div className={'summary-tws-description'}
                        dangerouslySetInnerHTML={{ __html: tankDescription }} />
                </ExpandableContainer>}
        </>
    }

    private renderTwsGeneralDescription() {
        const TWSDescription = this.state.selectedWaterSystem?.description;
        return <>{TWSDescription &&
            <ExpandableContainer arrowFirst={true}
                label={__('TWS general description')}
                initialOpen={true}>
                <div className={'summary-tws-description'}
                    dangerouslySetInnerHTML={{ __html: TWSDescription }} />
            </ExpandableContainer>}
        </>
    }

    private renderProductPictures() {
        const tankImage = this.state.selectedTank?.image;
        const TWSImage = this.state.selectedWaterSystem?.image;
        if (!tankImage && !TWSImage) return <></>;
        return <>
            <ExpandableContainer arrowFirst={true}
                label={__('Product pictures')}
                initialOpen={true}>
                <div className={`summary-product-pictures`}>
                    {TWSImage && <div>
                        <div className={'tws-summary-product-background-picture'} style={{ backgroundImage: `url(${TWSImage})` }} />
                    </div>}
                    {tankImage && <div>
                        <div className={'tws-summary-product-background-picture'} style={{ backgroundImage: `url(${tankImage})` }} />
                    </div>}
                </div>
            </ExpandableContainer>
        </>
    }

    private renderTechnicalDrawings() {
        const tankImage = this.state.selectedTank?.technicalDrawing;
        const TWSImage = this.state.selectedWaterSystem?.technicalDrawing;
        if (!tankImage && !TWSImage) return <></>;
        return <>
            <ExpandableContainer arrowFirst={true}
                label={__('Technical drawings')}
                initialOpen={true}>
                <div style={{ padding: '16px', height: '500px' }}>
                    {TWSImage && <div className={'tws-summary-product-background-picture'} style={{ backgroundImage: `url(${TWSImage})` }} />}
                    {tankImage && <div className={'tws-summary-product-background-picture'} style={{ backgroundImage: `url(${tankImage})` }} />}
                </div>
            </ExpandableContainer>
        </>
    }
}

